
import { NodeType, AgentType } from './types';

export const NODE_COLORS: Record<NodeType, string> = {
  [NodeType.ENTRANCE]: '#22c55e', 
  [NodeType.AMBULANCE_ENTRANCE]: '#dc2626', 
  [NodeType.RECEPTION]: '#0ea5e9', 
  [NodeType.TRIAGE]: '#eab308', 
  [NodeType.WAITING]: '#64748b', 
  [NodeType.EXAM_ROOM]: '#3b82f6', 
  [NodeType.PROCEDURE_ROOM]: '#818cf8', 
  [NodeType.UTILITY_ROOM]: '#334155', 
  [NodeType.ISOLATION_ROOM]: '#f97316', 
  [NodeType.TRAUMA_BAY]: '#b91c1c', 
  [NodeType.RADIOLOGY]: '#7e22ce', 
  [NodeType.LABORATORY]: '#be185d', 
  [NodeType.PHARMACY]: '#14b8a6', 
  [NodeType.WARD]: '#06b6d4', 
  [NodeType.HALLWAY]: '#475569', 
  [NodeType.STAFF_STATION]: '#ec4899', 
  [NodeType.STAFF_REST]: '#a16207', 
  [NodeType.WAYPOINT]: '#94a3b8', 
  [NodeType.BED]: '#06b6d4',
  [NodeType.ELEVATOR]: '#1e293b',
  [NodeType.ARMCHAIRS]: '#a78bfa',
  [NodeType.VIP_ZONE]: '#fbbf24',
  [NodeType.SECURITY_POST]: '#1e40af',
  [NodeType.CONFERENCE_HALL]: '#6366f1',
  [NodeType.SOCIAL_ROOM]: '#fda4af',
  [NodeType.WC]: '#cbd5e1',
  [NodeType.CALL_CENTER]: '#4f46e5',
  [NodeType.STATISTICS]: '#8b5cf6',
  [NodeType.TELEMEDICINE]: '#06fdf2',
  [NodeType.ADMIN]: '#4338ca',
  [NodeType.CUSTOM]: '#ffffff',
  [NodeType.CLOAKROOM]: '#78350f',
  [NodeType.WASHING]: '#38bdf8',
  // Fix: Added missing color for DOOR node type
  [NodeType.DOOR]: '#f87171',
};

export const AGENT_COLORS: Record<AgentType, string> = {
  [AgentType.PATIENT_WAL_IN]: '#93c5fd', 
  [AgentType.PATIENT_TRAUMA]: '#ef4444', 
  [AgentType.PATIENT_STROKE]: '#a855f7', 
  [AgentType.PATIENT_EMS]: '#fb923c', 
  [AgentType.PATIENT_INFECTIOUS]: '#84cc16', 
  [AgentType.COMPANION]: '#cbd5e1', 
  [AgentType.DOCTOR]: '#ffffff', 
  [AgentType.NURSE]: '#f472b6', 
  [AgentType.NURSE_ASSISTANT]: '#10b981', 
  [AgentType.SPECIALIST_TEAM]: '#f59e0b', 
};

export const NODE_RADIUS = 12;
export const WAYPOINT_RADIUS = 2; 
export const CONNECTION_WIDTH = 2;
export const SIMULATION_TICK_RATE = 50; 

export const TICKS_PER_MINUTE = 60000 / SIMULATION_TICK_RATE;
export const DEFAULT_PIXELS_PER_METER = 20; 

export const INFECTION_RADIUS = 30; 
export const INFECTION_PROBABILITY = 0.05; 
export const NODE_CONTAMINATION_RATE = 0.5; 
export const NODE_DECONTAMINATION_RATE = 0.1; 

export const MAX_PATIENCE_TICKS = 2000; 
export const TREATMENT_TICKS = 200; 

export const DEFAULT_NODE_CONFIGS: Record<NodeType, { processingTime: number, speedModifier: number, capacity: number }> = {
    [NodeType.ENTRANCE]: { processingTime: 0, speedModifier: 1.0, capacity: 10 },
    [NodeType.AMBULANCE_ENTRANCE]: { processingTime: 0, speedModifier: 1.0, capacity: 5 },
    [NodeType.RECEPTION]: { processingTime: 120, speedModifier: 1.0, capacity: 2 },
    [NodeType.TRIAGE]: { processingTime: 100, speedModifier: 1.0, capacity: 1 }, 
    [NodeType.WAITING]: { processingTime: 0, speedModifier: 0.8, capacity: 20 }, 
    [NodeType.EXAM_ROOM]: { processingTime: 300, speedModifier: 1.0, capacity: 1 }, 
    [NodeType.PROCEDURE_ROOM]: { processingTime: 450, speedModifier: 1.0, capacity: 2 }, 
    [NodeType.UTILITY_ROOM]: { processingTime: 0, speedModifier: 0.9, capacity: 5 }, 
    [NodeType.ISOLATION_ROOM]: { processingTime: 800, speedModifier: 1.0, capacity: 1 }, 
    [NodeType.TRAUMA_BAY]: { processingTime: 600, speedModifier: 1.0, capacity: 2 }, 
    [NodeType.RADIOLOGY]: { processingTime: 300, speedModifier: 1.0, capacity: 1 }, 
    [NodeType.LABORATORY]: { processingTime: 150, speedModifier: 1.0, capacity: 2 }, 
    [NodeType.PHARMACY]: { processingTime: 100, speedModifier: 1.0, capacity: 5 }, 
    [NodeType.WARD]: { processingTime: 1000, speedModifier: 1.0, capacity: 6 }, 
    [NodeType.HALLWAY]: { processingTime: 0, speedModifier: 1.2, capacity: 10 }, 
    [NodeType.STAFF_STATION]: { processingTime: 0, speedModifier: 1.0, capacity: 5 },
    [NodeType.STAFF_REST]: { processingTime: 0, speedModifier: 1.0, capacity: 12 },
    [NodeType.WAYPOINT]: { processingTime: 0, speedModifier: 1.0, capacity: 999 }, 
    [NodeType.BED]: { processingTime: 500, speedModifier: 1.0, capacity: 1 },
    [NodeType.ELEVATOR]: { processingTime: 300, speedModifier: 1.0, capacity: 4 },
    [NodeType.ARMCHAIRS]: { processingTime: 400, speedModifier: 0.9, capacity: 4 },
    [NodeType.VIP_ZONE]: { processingTime: 0, speedModifier: 1.0, capacity: 10 },
    [NodeType.SECURITY_POST]: { processingTime: 0, speedModifier: 1.0, capacity: 2 },
    [NodeType.CONFERENCE_HALL]: { processingTime: 0, speedModifier: 0.8, capacity: 30 },
    [NodeType.SOCIAL_ROOM]: { processingTime: 0, speedModifier: 1.0, capacity: 15 },
    [NodeType.WC]: { processingTime: 180, speedModifier: 1.0, capacity: 1 },
    [NodeType.CALL_CENTER]: { processingTime: 0, speedModifier: 1.0, capacity: 20 },
    [NodeType.STATISTICS]: { processingTime: 0, speedModifier: 1.0, capacity: 10 },
    [NodeType.TELEMEDICINE]: { processingTime: 600, speedModifier: 1.0, capacity: 1 },
    [NodeType.ADMIN]: { processingTime: 0, speedModifier: 1.0, capacity: 15 },
    [NodeType.CUSTOM]: { processingTime: 0, speedModifier: 1.0, capacity: 10 },
    [NodeType.CLOAKROOM]: { processingTime: 60, speedModifier: 1.0, capacity: 15 },
    [NodeType.WASHING]: { processingTime: 600, speedModifier: 1.0, capacity: 2 },
    // Fix: Added missing configuration for DOOR node type
    [NodeType.DOOR]: { processingTime: 0, speedModifier: 1.0, capacity: 999 },
};
